// src/dev/mockTeams.ts
//
// Dev-mock fixtures for the team endpoints (/api/teams, /api/team-members, /api/team-invite-info), so
// Settings › Team, the /join/:token landing, and the project-creation team picker are all reachable
// with no backend. Mutations mutate this module's state, which is exactly what makes the tab feel
// real in mock mode: an invite appears in the roster, a role change sticks, leaving removes you.
//
// The fixture deliberately covers all three roles a user can hold — owner of their own team, admin of
// someone else's, and plain member of a third — because the interesting UI states only exist when all
// of them are present: what an admin may change (everything but billing), what a member is not
// offered at all, and what "leave" does.

import { MOCK_AUTH_USER } from './mockAuth'
// Re-exported so existing importers (mockBilling) keep one place to ask; defined apart to keep this
// module and mockPipeline from importing each other.
import { MOCK_TEAM_IDS } from './mockTeamIds'
export { MOCK_TEAM_IDS }
import { planIncludesTeamCollaboration } from '../lib/stripe-config'
import type { Team, TeamMember, TeamRole } from '../data/projectTypes'

interface MockTeam {
  id: string
  name: string
  ownerUserId: string
  inviteToken: string | null
}

function iso(daysAgo: number): string {
  return new Date(Date.now() - daysAgo * 86_400_000).toISOString()
}


/**
 * The plan funding each mock team. One per entitlement state, because that is what the Team tab
 * branches on: Pro can invite, Free and Starter get the upsell instead. The billing ledger reads
 * these too (mockBilling), so a team's plan name, credits and invite controls always agree.
 */
export const MOCK_TEAM_PLAN_KEYS: Record<string, string> = {
  // Free: the owner of this one gets the upgrade teaser where the invite controls would be.
  [MOCK_TEAM_IDS.own]: 'free-v1',
  [MOCK_TEAM_IDS.member]: 'team-v1',
  // Pro, so the invite-by-email and invite-link UI is reachable in mock mode — as an admin, which is
  // also the role that exercises team billing.
  [MOCK_TEAM_IDS.admin]: 'team-v1',
}

/** Mirrors the server gate in netlify/functions/lib/team-plan.ts. */
export function mockTeamCanInvite(teamId: string): boolean {
  return planIncludesTeamCollaboration(MOCK_TEAM_PLAN_KEYS[teamId] ?? null)
}

/** Which team a self-join link belongs to, so the plan gate can run before anyone is added. */
export function mockTeamIdForInviteToken(token: string): string | null {
  return teams.find((team) => team.inviteToken === token)?.id ?? null
}

const teams: MockTeam[] = [
  { id: MOCK_TEAM_IDS.own, name: 'Local Dev’s Team', ownerUserId: MOCK_AUTH_USER.id, inviteToken: 'mock-invite-token' },
  { id: MOCK_TEAM_IDS.member, name: 'Acme QA', ownerUserId: 'mock-user-001', inviteToken: null },
  { id: MOCK_TEAM_IDS.admin, name: 'Northwind Web', ownerUserId: 'mock-user-003', inviteToken: 'mock-invite-northwind' },
]

let members: TeamMember[] = [
  {
    id: 'tm-own-owner',
    team_id: 'team-dev-own',
    user_id: MOCK_AUTH_USER.id,
    email: MOCK_AUTH_USER.email,
    name: MOCK_AUTH_USER.name,
    role: 'owner',
    created_at: iso(120),
  },
  {
    id: 'tm-own-admin',
    team_id: 'team-dev-own',
    user_id: 'mock-user-002',
    email: 'ava.chen@acme.com',
    name: 'Ava Chen',
    role: 'admin',
    created_at: iso(40),
  },
  {
    // A still-pending invite: no session yet, so no display name — the roster shows it as invited.
    id: 'tm-own-pending',
    team_id: 'team-dev-own',
    user_id: 'pending-sam@example.local',
    email: 'sam@example.local',
    name: null,
    role: 'member',
    created_at: iso(2),
  },
  {
    id: 'tm-acme-owner',
    team_id: 'team-acme',
    user_id: 'mock-user-001',
    email: 'owner@acme.com',
    name: 'Acme Owner',
    role: 'owner',
    created_at: iso(200),
  },
  {
    id: 'tm-acme-me',
    team_id: 'team-acme',
    user_id: MOCK_AUTH_USER.id,
    email: MOCK_AUTH_USER.email,
    name: MOCK_AUTH_USER.name,
    role: 'member',
    created_at: iso(15),
  },
  {
    id: 'tm-northwind-owner',
    team_id: 'team-northwind',
    user_id: 'mock-user-003',
    email: 'dana@northwind.example',
    name: 'Dana Okafor',
    role: 'owner',
    created_at: iso(90),
  },
  {
    // The viewer as an ADMIN: may rename the team and manage the roster, but not its billing.
    id: 'tm-northwind-me',
    team_id: 'team-northwind',
    user_id: MOCK_AUTH_USER.id,
    email: MOCK_AUTH_USER.email,
    name: MOCK_AUTH_USER.name,
    role: 'admin',
    created_at: iso(30),
  },
  {
    id: 'tm-northwind-member',
    team_id: 'team-northwind',
    user_id: 'mock-user-004',
    email: 'kai@northwind.example',
    name: 'Kai Bergström',
    role: 'member',
    created_at: iso(9),
  },
]

function roleFor(teamId: string): TeamRole | null {
  return members.find((m) => m.team_id === teamId && m.user_id === MOCK_AUTH_USER.id)?.role ?? null
}

function canManage(role: TeamRole | null): boolean {
  return role === 'owner' || role === 'admin'
}

function toSummary(team: MockTeam): Team {
  const role = roleFor(team.id) ?? 'member'
  const manage = canManage(role)
  return {
    id: team.id,
    name: team.name,
    role,
    member_count: members.filter((m) => m.team_id === team.id).length,
    is_owner: role === 'owner',
    can_manage: manage,
    can_manage_billing: manage,
    invite_url: manage && team.inviteToken ? `${window.location.origin}/join/${team.inviteToken}` : null,
  }
}

export function mockFetchTeams(): { items: Team[] } {
  const mine = teams.filter((team) => roleFor(team.id) !== null)
  // Own team first, matching the server's ordering.
  const ordered = [...mine].sort((a, b) => {
    const own = Number(b.ownerUserId === MOCK_AUTH_USER.id) - Number(a.ownerUserId === MOCK_AUTH_USER.id)
    return own !== 0 ? own : a.name.localeCompare(b.name)
  })
  return { items: ordered.map(toSummary) }
}

/**
 * The billing portal, minus Stripe. The member case returns the same 403 shape the real endpoint
 * does, so the Team tab's error path is exercisable without a backend.
 */
export function mockTeamBillingPortal(teamId: string): { status: number; body: unknown } {
  const role = roleFor(teamId)
  if (!canManage(role)) {
    return {
      status: 403,
      body: { error: 'Only a team owner or admin can manage the team’s billing' },
    }
  }
  return { status: 200, body: { url: `${window.location.origin}/settings/team?portal=mock` } }
}

export function mockRenameTeam(teamId: string, name: string): { id: string; name: string } | null {
  const team = teams.find((t) => t.id === teamId)
  if (!team) return null
  team.name = name
  return { id: team.id, name: team.name }
}

export function mockUpdateTeamInvite(teamId: string, action: string): { invite_url: string | null } {
  const team = teams.find((t) => t.id === teamId)
  if (!team) return { invite_url: null }
  team.inviteToken = action === 'revoke-invite' ? null : `mock-invite-${Math.random().toString(36).slice(2, 8)}`
  return { invite_url: team.inviteToken ? `${window.location.origin}/join/${team.inviteToken}` : null }
}

export function mockFetchTeamMembers(teamId: string): { items: TeamMember[]; role: TeamRole } {
  const order: Record<TeamRole, number> = { owner: 0, admin: 1, member: 2 }
  const items = members
    .filter((m) => m.team_id === teamId)
    .sort((a, b) => order[a.role] - order[b.role] || a.created_at.localeCompare(b.created_at))
  return { items, role: roleFor(teamId) ?? 'member' }
}

export function mockInviteTeamMember(teamId: string, email: string, role?: TeamRole): TeamMember {
  const existing = members.find((m) => m.team_id === teamId && m.email?.toLowerCase() === email.toLowerCase())
  if (existing) return existing
  const member: TeamMember = {
    id: `tm-${Math.random().toString(36).slice(2, 8)}`,
    team_id: teamId,
    user_id: `pending-${email.toLowerCase()}`,
    email: email.toLowerCase(),
    name: null,
    role: role ?? 'member',
    created_at: new Date().toISOString(),
  }
  members = [...members, member]
  return member
}

export function mockSetTeamMemberRole(memberId: string, role: TeamRole): TeamMember | null {
  const member = members.find((m) => m.id === memberId)
  if (!member || member.role === 'owner') return null
  member.role = role
  return member
}

export function mockRemoveTeamMember(memberId: string): void {
  members = members.filter((m) => m.id !== memberId || m.role === 'owner')
}

export function mockTeamInviteInfo(token: string): { id: string; name: string; member_count: number } | null {
  const team = teams.find((t) => t.inviteToken === token)
  // Any other token still resolves in mock mode, so the /join landing is reachable from a made-up
  // URL while developing it. A real deployment 404s an unknown token.
  const target = team ?? teams[1]
  if (!target) return null
  return { id: target.id, name: target.name, member_count: members.filter((m) => m.team_id === target.id).length }
}

export function mockJoinTeam(token: string): { team: { id: string; name: string }; member: TeamMember } | null {
  const info = mockTeamInviteInfo(token)
  if (!info) return null
  const already = members.find((m) => m.team_id === info.id && m.user_id === MOCK_AUTH_USER.id)
  const member = already ?? {
    id: `tm-${Math.random().toString(36).slice(2, 8)}`,
    team_id: info.id,
    user_id: MOCK_AUTH_USER.id,
    email: MOCK_AUTH_USER.email,
    name: MOCK_AUTH_USER.name,
    role: 'member' as TeamRole,
    created_at: new Date().toISOString(),
  }
  if (!already) members = [...members, member]
  return { team: { id: info.id, name: info.name }, member }
}
